import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { SharedModule } from "../../shared/shared.module";
import { EmployeesService } from "../../employees/employees.service";
import type {
  DialogEmployee,
  PrintCardBulkDownloadDialogData,
  PrintCardBulkDownloadDialogResult,
  PrintCardPageSizePreset,
} from "../print-cards.interface";

@Component({
  selector: "app-print-card-bulk-download-dialog",
  templateUrl: "./print-card-bulk-download-dialog.component.html",
  styleUrls: ["./print-card-bulk-download-dialog.component.scss"],
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule],
})
export class PrintCardBulkDownloadDialogComponent implements OnInit, OnDestroy {
  readonly pageSizePresetControl = new FormControl<string>("", {
    nonNullable: true,
  });
  readonly searchControl = new FormControl<string>("", { nonNullable: true });

  allEmployees: DialogEmployee[] = [];
  filteredEmployees: DialogEmployee[] = [];
  selectedEmployees: DialogEmployee[] = [];
  isLoadingEmployees = false;

  private searchSub?: Subscription;

  constructor(
    private readonly dialogRef: MatDialogRef<
      PrintCardBulkDownloadDialogComponent,
      PrintCardBulkDownloadDialogResult
    >,
    @Inject(MAT_DIALOG_DATA)
    public readonly data: PrintCardBulkDownloadDialogData,
    private readonly employeesService: EmployeesService,
  ) {
    const defaultPresetId =
      data.selectedPresetId?.trim() || data.presets[0]?.id || "";
    const presetExists = data.presets.some(
      (preset) => preset.id === defaultPresetId,
    );
    this.pageSizePresetControl.setValue(
      presetExists ? defaultPresetId : (data.presets[0]?.id ?? ""),
      { emitEvent: false },
    );
  }

  ngOnInit(): void {
    this.loadEmployees();
    this.searchSub = this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.filterEmployees());
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  get presets(): PrintCardPageSizePreset[] {
    return this.data.presets;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    const emails = this.selectedEmployees
      .map((employee) => employee.email?.trim())
      .filter((email): email is string => Boolean(email));
    if (emails.length === 0) {
      return;
    }
    this.dialogRef.close({
      pageSizePresetId: this.pageSizePresetControl.value,
      emails,
    });
  }

  toggleEmployeeSelection(employee: DialogEmployee): void {
    const index = this.selectedEmployees.findIndex(
      (item) => item.employeeId === employee.employeeId,
    );
    if (index === -1) {
      this.selectedEmployees.push(employee);
    } else {
      this.selectedEmployees.splice(index, 1);
    }
  }

  isEmployeeSelected(employee: DialogEmployee): boolean {
    return this.selectedEmployees.some(
      (item) => item.employeeId === employee.employeeId,
    );
  }

  isAllSelected(): boolean {
    return (
      this.filteredEmployees.length > 0 &&
      this.filteredEmployees.every((employee) =>
        this.isEmployeeSelected(employee),
      )
    );
  }

  masterToggle(): void {
    if (this.isAllSelected()) {
      this.filteredEmployees.forEach((employee) => {
        const index = this.selectedEmployees.findIndex(
          (item) => item.employeeId === employee.employeeId,
        );
        if (index !== -1) {
          this.selectedEmployees.splice(index, 1);
        }
      });
    } else {
      this.filteredEmployees.forEach((employee) => {
        if (!this.isEmployeeSelected(employee)) {
          this.selectedEmployees.push(employee);
        }
      });
    }
  }

  private loadEmployees(): void {
    this.isLoadingEmployees = true;
    this.employeesService.getNotExcludedEmployeesWithAliases().subscribe({
      next: (rows) => {
        this.allEmployees = rows
          .filter((employee) => Boolean(employee.employeeId))
          .filter((employee) => Boolean(employee.email?.trim()));
        this.selectedEmployees = [...this.allEmployees];
        this.filterEmployees();
        this.isLoadingEmployees = false;
      },
      error: () => {
        this.allEmployees = [];
        this.filteredEmployees = [];
        this.selectedEmployees = [];
        this.isLoadingEmployees = false;
      },
    });
  }

  private filterEmployees(): void {
    const searchValue = this.searchControl.value.trim().toLowerCase();
    if (!searchValue) {
      this.filteredEmployees = [...this.allEmployees];
      return;
    }
    this.filteredEmployees = this.allEmployees.filter((employee) => {
      return (
        employee.fullName?.toLowerCase().includes(searchValue) ||
        employee.email?.toLowerCase().includes(searchValue)
      );
    });
  }
}
